import type { IpFilter } from '@nx/state';
import { getApiError, getErrorMessage } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import type { IpFilterData } from './types';

// Validates an IP address in CIDR notation
// Appends the '32' prefix if not present, indicating an individual IP address
export const isValidIpRange = (ipRange: string): boolean => {
  const [address, prefixLen] = ipRange.split('/');

  if (address === undefined) {
    return false;
  }

  const prefix = parseInt(prefixLen ?? '32', 10);
  if (isNaN(prefix) || prefix < 0 || prefix > 32) {
    return false;
  }

  const ipv4 = /^(\d{1,3}\.){3}\d{1,3}$/;
  const ipv6 = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;

  if ((!ipv4.test(address) && !ipv6.test(address)) || ipRange === '0.0.0.0/0') {
    return false;
  }

  return true;
};

// Strips '32' prefix of ranges indicating an individual IP address
export const cleanCIDR = (cidr: string): string => {
  const [address, prefixLen] = cidr.split('/');

  if (address === undefined) {
    return cidr;
  }

  if (isNullish(prefixLen) || prefixLen === '32') {
    return address;
  }

  return cidr;
};

export const parseErrorResponse = (error: FetchBaseQueryError | SerializedError | undefined): string => {
  const apiError = getApiError(error);
  let errorMessage = getErrorMessage(apiError);

  // Check for and parse the validation errors
  if (
    apiError.reason === 'validation-error' &&
    error &&
    'data' in error &&
    isNotNullish(error.data) &&
    typeof error.data === 'object' &&
    'errors' in error.data
  ) {
    const dataErrors = error.data.errors;
    if (
      isNotNullish(dataErrors) &&
      typeof dataErrors === 'object' &&
      Array.isArray(dataErrors) &&
      dataErrors.every((value) => typeof value === 'string')
    ) {
      errorMessage = `Validation Error: ${dataErrors.join(', ')}`;
    }
  }

  return errorMessage;
};

const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  a.sort();
  b.sort();
  return a.length === b.length && a.every((element, index) => element === b[index]);
};

export const nameDifferent = (data: IpFilterData, ipFilter: IpFilter) => data.name?.trim() !== ipFilter.name;

export const descriptionDifferent = (data: IpFilterData, ipFilter: IpFilter) =>
  data.description !== ipFilter.description;

export const allowListDifferent = (data: IpFilterData, ipFilter: IpFilter) =>
  !arraysEqual(
    data.allowList ? data.allowList.map((ip) => `${ip.IpRange}/${ip.Description}`) : [],
    ipFilter.allowList.map((ip) => `${ip.address}/${ip.prefixLen}/${ip.description}`),
  );

// Check entities are different
export const entitiesDifferent = (data: IpFilterData, ipFilter: IpFilter) =>
  !arraysEqual(data.filteredEntities?.instances ?? [], ipFilter.filteredEntities?.instances ?? []) ||
  !arraysEqual(data.filteredEntities?.projects ?? [], ipFilter.filteredEntities?.projects ?? []) ||
  !arraysEqual(data.filteredEntities?.organizations ?? [], ipFilter.filteredEntities?.organizations ?? []);

export const formHasChanged = (data: IpFilterData, ipFilter: IpFilter): boolean => {
  return (
    nameDifferent(data, ipFilter) ||
    descriptionDifferent(data, ipFilter) ||
    allowListDifferent(data, ipFilter) ||
    entitiesDifferent(data, ipFilter)
  );
};
